import { Button, Card } from "@peer-start/ui/reducate";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  competencyCompletion,
  competencyStatus,
  highlightedCompetencyId,
  startingTeacherRoute,
  type StartingTeacherProgress,
} from "../data/starting-teacher-route.js";
import { StartingTeacherRouteFrame } from "./StartingTeacherRouteFrame.js";
import { StartingTeacherStatusPill } from "./StartingTeacherStatusPill.js";

export function StartingTeacherOverview({
  progress,
  aside,
  onOpen,
  onContinue,
}: {
  progress: StartingTeacherProgress;
  aside: ReactNode;
  onOpen: (competencyId: string) => void;
  onContinue: (competencyId: string) => void;
}) {
  const { t } = useTranslation();
  const activeId = highlightedCompetencyId(progress);

  return (
    <StartingTeacherRouteFrame
      aside={aside}
      controlId="starting-teacher.overview"
      header={
        <header>
          <h1 className="text-3xl font-medium tracking-tight text-foreground">
            {t("learningRoute.routeTitle")}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {t("learningRoute.subtitle")}
          </p>
        </header>
      }
    >
      <ol className="space-y-3" data-control-id="starting-teacher.competencies">
        {startingTeacherRoute.map((competency) => {
          const active = competency.id === activeId;
          const status = competencyStatus(competency, progress, active);
          const filled = status === "active";
          const counts = competencyCompletion(competency, progress);
          const title = t(`startingTeacher.competencies.${competency.id}.title`);
          return (
            <li key={competency.id}>
              <Card
                className={
                  filled ? "bg-primary-deep text-primary-foreground ring-primary-deep shadow-md" : undefined
                }
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-xs">
                      <StartingTeacherStatusPill onPrimary={filled} status={status} />
                      <span className={filled ? "text-primary-foreground/80" : "text-muted-foreground"}>
                        {t("startingTeacher.skillsDone", counts)}
                      </span>
                    </p>
                    <h2 className="mt-4 text-lg font-medium">{title}</h2>
                    <p
                      className={`mt-2 max-w-2xl text-sm leading-relaxed ${
                        filled ? "text-primary-foreground/80" : "text-muted-foreground"
                      }`}
                    >
                      {t(`startingTeacher.competencies.${competency.id}.summary`)}
                    </p>
                  </div>
                  {filled ? (
                    <Button
                      aria-label={`${t("startingTeacher.continueRoute")}: ${title}`}
                      className="shrink-0"
                      controlId={`starting-teacher.competency.${competency.id}.continue`}
                      onClick={() => onContinue(competency.id)}
                      type="button"
                    >
                      {t("startingTeacher.continueRoute")}
                    </Button>
                  ) : (
                    <Button
                      aria-label={`${t(active ? "startingTeacher.continueRoute" : "startingTeacher.jumpTo")}: ${title}`}
                      className="shrink-0"
                      controlId={`starting-teacher.competency.${competency.id}.${active ? "continue" : "open"}`}
                      onClick={() => (active ? onContinue(competency.id) : onOpen(competency.id))}
                      type="button"
                      variant={active ? "primary" : "secondary"}
                    >
                      {t(active ? "startingTeacher.continueRoute" : "startingTeacher.jumpTo")}
                    </Button>
                  )}
                </div>
              </Card>
            </li>
          );
        })}
      </ol>
    </StartingTeacherRouteFrame>
  );
}
